import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { UserRole } from '../types';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  instructorId: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  signUp: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  signUp: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserRole = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setUser(null);
      setRole(null);
      setIsLoading(false);
      return;
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const response = await fetch('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'X-MSAL-Token': tokenResponse.accessToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.id || accounts[0].localAccountId,
          name: data.name || accounts[0].name || '',
          email: data.email || accounts[0].username || '',
          role: data.role as UserRole,
          instructorId: data.instructorId || null,
        });
        setRole(data.role as UserRole);
      } else {
        setUser({
          id: accounts[0].localAccountId,
          name: accounts[0].name || '',
          email: accounts[0].username || '',
          role: 'customer',
          instructorId: null,
        });
        setRole('customer');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      if (accounts.length > 0) {
        setUser({
          id: accounts[0].localAccountId,
          name: accounts[0].name || '',
          email: accounts[0].username || '',
          role: 'customer',
          instructorId: null,
        });
        setRole('customer');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, accounts, instance]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      fetchUserRole();
    }
  }, [inProgress, fetchUserRole]);

  const login = () => {
    instance.loginRedirect(loginRequest).catch(console.error);
  };

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    }).catch(console.error);
  };

  const signUp = () => {
    instance.loginRedirect({
      ...loginRequest,
      prompt: 'create',
    }).catch(console.error);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        isAuthenticated,
        isLoading,
        login,
        logout,
        signUp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
